import React from 'react';
import styled from 'styled-components';
import { FlexColumn, FlexRow } from '@make-software/cspr-ui';
import { HeaderText, BodyText } from '@block-explorer/ui';
import Cspr from '../cspr/cspr';
import { useCurrencyRateAmount } from '../../hooks/event-store-hooks';
import { motesToCurrency, PrecisionCase } from '../../utils/currency';
import FiatCurrency from '../multi-currency/multi-currency';
import { useSelector } from 'react-redux';
import { RootState } from 'typesafe-actions';

const StyledFlexColumn = styled(FlexColumn)<{ showBottomBorder }>(
  ({ theme, showBottomBorder }) => ({
    borderBottom: showBottomBorder
      ? '1px solid ' + theme.styleguideColors.borderPrimary
      : 'none',
    padding: showBottomBorder ? '0 0 16px' : '0',
  })
);

const StyledFlexRow = styled(FlexRow)<PriceRowProps>(
  ({ theme, showTopBorder }) => ({
    borderTop: showTopBorder
      ? '1px solid ' + theme.styleguideColors.borderPrimary
      : 'none',
    padding: showTopBorder ? '16px 0 0' : '0',
  })
);

const StyledCurrencyFlexRow = styled(FlexRow)(() => ({
  margin: 0,
}));

const DecoratedAmount = ({ isTotal, children }) => {
  return isTotal ? (
    <HeaderText size={3}>{children}</HeaderText>
  ) : (
    <BodyText scale={'sm'} size={1}>
      {children}
    </BodyText>
  );
};

export interface PriceRowProps {
  label?: JSX.Element | string;
  motes: string;
  isTotal?: boolean;
  showTopBorder?: boolean;
  showBottomBorder?: boolean;
}

export function PriceRow(props: PriceRowProps) {
  const { label, motes, isTotal = false, showTopBorder = true } = props;
  const { selectedCurrency: currency } = useSelector(
    (state: RootState) => state.currencies
  );
  // NOTE: it should be 8 because otherwise the transfer cost is rounded to 0.
  const PRECISION = 8;

  const { data: rateData, loading } = useCurrencyRateAmount({
    currencyCode: currency.id.toString(),
  });

  const safeMotesValue = motes || '0';

  const transferCostAmount =
    rateData?.data?.amount &&
    motesToCurrency(safeMotesValue, rateData?.data.amount);

  return (
    <StyledFlexColumn showBottomBorder={props.showBottomBorder}>
      <StyledFlexRow
        justify="space-between"
        {...props}
        showTopBorder={showTopBorder}
      >
        <BodyText variation="darkGray" size={3}>
          {label}
        </BodyText>
        <BodyText size={3} monotype>
          <Cspr
            motes={safeMotesValue}
            precisionCase={PrecisionCase.deployCost}
          />
        </BodyText>
      </StyledFlexRow>
      <StyledCurrencyFlexRow justify="flex-end">
        <DecoratedAmount isTotal={isTotal}>
          {loading ? (
            'Loading...'
          ) : (
            <FiatCurrency
              amount={transferCostAmount || '0'}
              precision={PRECISION}
            />
          )}
        </DecoratedAmount>
      </StyledCurrencyFlexRow>
    </StyledFlexColumn>
  );
}
export default PriceRow;
